import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Gift, Check, Plus, Minus, Calendar, ShieldCheck, CreditCard, CheckCircle, ArrowLeft, Snowflake } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HolidayMenuPage() {
  const { lang } = useLanguage();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [extras, setExtras] = useState({});
  const [pickupDate, setPickupDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/holiday-menu`).then(r => {
      if (r.data && r.data.active) setMenu(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getExtraQty = (id) => extras[id] || 0;
  const setExtraQty = (id, val) => setExtras(prev => ({ ...prev, [id]: Math.max(0, val) }));

  const calcTotal = () => {
    if (!selectedPkg) return 0;
    const pkg = menu.packages.find(p => p.id === selectedPkg);
    let total = (pkg?.price || 0) * quantity;
    (menu.extras || []).forEach(ex => {
      total += (getExtraQty(ex.id)) * ex.price;
    });
    return total;
  };

  const taxRate = 0.14975;
  const subtotal = calcTotal();
  const taxes = subtotal * taxRate;
  const total = subtotal + taxes;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPkg || !pickupDate) {
      toast.error(lang === 'fr' ? 'Veuillez choisir un forfait et une date de ramassage' : 'Please select a package and pickup date');
      return;
    }
    setSubmitting(true);
    try {
      const pkg = menu.packages.find(p => p.id === selectedPkg);
      const selectedExtras = (menu.extras || []).filter(ex => getExtraQty(ex.id) > 0).map(ex => ({
        id: ex.id,
        name: lang === 'fr' ? ex.name_fr : ex.name_en,
        price: ex.price,
        quantity: getExtraQty(ex.id),
      }));
      const { data } = await axios.post(`${API}/holiday-menu/orders`, {
        ...form,
        package_id: selectedPkg,
        package_name: lang === 'fr' ? pkg.name_fr : pkg.name_en,
        package_price: pkg.price,
        quantity,
        extras: selectedExtras,
        pickup_date: pickupDate,
        subtotal,
        taxes,
        total,
      });
      setOrderResult(data);
      toast.success(lang === 'fr' ? 'Commande confirmée!' : 'Order confirmed!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-24 pb-20 text-center text-[#555555]">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;
  if (!menu) return <div className="pt-24 pb-20 text-center text-[#555555]">{lang === 'fr' ? 'Le menu des fêtes n\'est pas disponible pour le moment.' : 'Holiday menu is not available right now.'}</div>;

  if (orderResult) {
    return (
      <div className="pt-20 lg:pt-24 pb-20 min-h-[60vh] flex items-center justify-center" data-testid="holiday-order-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md mx-auto px-4">
          <CheckCircle className="w-20 h-20 text-[#2E7D32] mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-bold text-[#1a1a1a] mb-3">{lang === 'fr' ? 'Commande confirmée!' : 'Order Confirmed!'}</h2>
          <p className="text-[#555555] mb-4">{lang === 'fr' ? 'Nous vous contacterons pour confirmer votre ramassage.' : 'We\'ll contact you to confirm your pickup.'}</p>
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 mb-6">
            <p className="text-sm text-[#555555] mb-1">{lang === 'fr' ? 'Numéro de commande' : 'Order Number'}</p>
            <p className="text-2xl font-bold text-[#e53a3a]">{orderResult.order_number}</p>
            <p className="text-sm text-[#555555] mt-2">{lang === 'fr' ? 'Ramassage' : 'Pickup'}: {orderResult.pickup_date}</p>
          </div>
          <a href="/" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold bg-[#e53a3a]" data-testid="holiday-back-home">
            <ArrowLeft className="w-4 h-4" /> {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="holiday-menu-page">
      {/* Hero */}
      <section className="bg-black text-white py-12 sm:py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 20% 50%, #e53a3a33 0%, transparent 50%), radial-gradient(circle at 80% 50%, #2E7D3233 0%, transparent 50%)' }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <Snowflake className="w-6 h-6 text-[#f4d00d]" />
              <Gift className="w-8 h-8 text-[#e53a3a]" />
              <Snowflake className="w-6 h-6 text-[#f4d00d]" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
              {lang === 'fr' ? menu.title_fr : menu.title_en}
            </h1>
            <p className="text-gray-300 text-sm sm:text-base max-w-xl mx-auto">
              {lang === 'fr' ? menu.subtitle_fr : menu.subtitle_en}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Note */}
        {(menu.note_fr || menu.note_en) && (
          <div className="bg-[#f4d00d]/10 border border-[#f4d00d]/30 rounded-xl p-4 mb-10 text-center text-sm text-[#1a1a1a]">
            {lang === 'fr' ? menu.note_fr : menu.note_en}
          </div>
        )}

        {/* Step 1: Choose Package */}
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#e53a3a] text-white flex items-center justify-center text-sm font-bold">1</span>
            {lang === 'fr' ? 'Choisissez votre boîte' : 'Choose your box'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(menu.packages || []).map((pkg) => (
              <motion.button
                key={pkg.id}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedPkg(pkg.id)}
                className={`text-left card-hover rounded-xl border-2 p-6 transition-all ${
                  selectedPkg === pkg.id ? 'border-[#e53a3a] bg-white shadow-lg' : 'border-[#e0e0e0] bg-white hover:border-[#e53a3a]/50'
                }`}
                data-testid={`pkg-${pkg.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg font-bold text-[#1a1a1a]">{lang === 'fr' ? pkg.name_fr : pkg.name_en}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-[#e53a3a]">{pkg.price}$</span>
                    {selectedPkg === pkg.id && <Check className="w-5 h-5 text-[#e53a3a]" />}
                  </div>
                </div>
                <ul className="space-y-1">
                  {(lang === 'fr' ? pkg.items_fr : pkg.items_en || []).map((item) => (
                    <li key={item} className="text-sm text-[#555555] flex items-start gap-2">
                      <span className="text-[#2E7D32] mt-0.5">&#10003;</span> {item}
                    </li>
                  ))}
                </ul>
              </motion.button>
            ))}
          </div>
          {selectedPkg && (
            <div className="mt-4 flex items-center gap-4">
              <Label className="text-sm font-medium">{lang === 'fr' ? 'Quantité de boîtes' : 'Number of boxes'}:</Label>
              <div className="flex items-center border border-[#e0e0e0] rounded-lg">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="p-2 hover:bg-[#f5f5f0] rounded-l-lg" data-testid="pkg-qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="px-4 font-semibold">{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="p-2 hover:bg-[#f5f5f0] rounded-r-lg" data-testid="pkg-qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Extras */}
        {(menu.extras || []).length > 0 && (
          <div className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#f4d00d] text-black flex items-center justify-center text-sm font-bold">2</span>
              {lang === 'fr' ? 'Ajoutez des extras' : 'Add extras'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(menu.extras || []).map((ex) => (
                <div key={ex.id} className="flex items-center justify-between bg-white rounded-xl border border-[#e0e0e0] p-4" data-testid={`extra-${ex.id}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-[#1a1a1a] truncate">{lang === 'fr' ? ex.name_fr : ex.name_en}</p>
                    <p className="text-sm font-semibold text-[#e53a3a]">{ex.price}$ / {lang === 'fr' ? 'unité' : 'unit'}</p>
                  </div>
                  <div className="flex items-center border border-[#e0e0e0] rounded-lg shrink-0">
                    <button onClick={() => setExtraQty(ex.id, getExtraQty(ex.id) - 1)} className="p-1.5 hover:bg-[#f5f5f0] rounded-l-lg"><Minus className="w-3 h-3" /></button>
                    <span className="px-3 text-sm font-medium w-8 text-center">{getExtraQty(ex.id)}</span>
                    <button onClick={() => setExtraQty(ex.id, getExtraQty(ex.id) + 1)} className="p-1.5 hover:bg-[#f5f5f0] rounded-r-lg"><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Pickup Date */}
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#2E7D32] text-white flex items-center justify-center text-sm font-bold">3</span>
            {lang === 'fr' ? 'Date de ramassage' : 'Pickup date'}
          </h2>
          <div className="flex flex-wrap gap-3">
            {(menu.pickup_dates || []).map((date) => {
              const d = new Date(date + 'T12:00:00');
              const label = d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <button
                  key={date}
                  onClick={() => setPickupDate(date)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                    pickupDate === date ? 'border-[#2E7D32] bg-[#2E7D32]/5 text-[#2E7D32]' : 'border-[#e0e0e0] bg-white text-[#1a1a1a] hover:border-[#2E7D32]/50'
                  }`}
                  data-testid={`pickup-${date}`}
                >
                  <Calendar className="w-4 h-4" />
                  {label}
                  {pickupDate === date && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 4: Info + Payment + Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center text-sm font-bold">4</span>
              {lang === 'fr' ? 'Vos informations' : 'Your information'}
            </h2>
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#e0e0e0] p-6 space-y-4" data-testid="holiday-order-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{lang === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                  <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} required className="mt-1" data-testid="holiday-name" />
                </div>
                <div>
                  <Label>{lang === 'fr' ? 'Courriel' : 'Email'}</Label>
                  <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} required className="mt-1" data-testid="holiday-email" />
                </div>
                <div>
                  <Label>{lang === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                  <Input type="tel" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} required className="mt-1" data-testid="holiday-phone" />
                </div>
                <div>
                  <Label>{lang === 'fr' ? 'Notes spéciales' : 'Special notes'}</Label>
                  <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1" data-testid="holiday-notes" />
                </div>
              </div>

              {/* Payment mock */}
              <div className="bg-[#f5f5f0] rounded-lg p-4 border border-[#e0e0e0]" data-testid="holiday-payment">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-[#2E7D32]" />
                  <span className="font-semibold text-sm text-[#1a1a1a]">{lang === 'fr' ? 'Paiement sécurisé' : 'Secure Payment'}</span>
                </div>
                <p className="text-xs text-[#555555]">{lang === 'fr' ? 'Paiement sécurisé par Élavon (en cours d\'intégration - paiement au comptoir lors du ramassage)' : 'Secure payment by Elavon (integration in progress - pay at counter during pickup)'}</p>
              </div>

              <button
                type="submit"
                disabled={submitting || !selectedPkg || !pickupDate}
                className="btn-belgo w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-lg bg-[#e53a3a] hover:bg-[#c62828] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="holiday-submit"
              >
                <CreditCard className="w-5 h-5" /> {submitting ? '...' : (lang === 'fr' ? 'Confirmer la commande' : 'Confirm Order')}
              </button>
            </form>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 sticky top-24" data-testid="holiday-summary">
              <h3 className="font-heading text-lg font-semibold text-[#1a1a1a] mb-4">{lang === 'fr' ? 'Résumé' : 'Summary'}</h3>
              {selectedPkg && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#555555]">{quantity}x {lang === 'fr' ? menu.packages.find(p => p.id === selectedPkg)?.name_fr : menu.packages.find(p => p.id === selectedPkg)?.name_en}</span>
                    <span className="font-medium">{(menu.packages.find(p => p.id === selectedPkg)?.price * quantity).toFixed(2)}$</span>
                  </div>
                  {(menu.extras || []).filter(ex => getExtraQty(ex.id) > 0).map(ex => (
                    <div key={ex.id} className="flex justify-between text-xs">
                      <span className="text-[#555555]">{getExtraQty(ex.id)}x {lang === 'fr' ? ex.name_fr : ex.name_en}</span>
                      <span>{(ex.price * getExtraQty(ex.id)).toFixed(2)}$</span>
                    </div>
                  ))}
                  <Separator className="my-2" />
                  <div className="flex justify-between"><span className="text-[#555555]">{lang === 'fr' ? 'Sous-total' : 'Subtotal'}</span><span>{subtotal.toFixed(2)}$</span></div>
                  <div className="flex justify-between"><span className="text-[#555555]">{lang === 'fr' ? 'Taxes (TPS+TVQ)' : 'Taxes (GST+QST)'}</span><span>{taxes.toFixed(2)}$</span></div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold"><span>{lang === 'fr' ? 'Total' : 'Total'}</span><span className="text-[#e53a3a]">{total.toFixed(2)}$</span></div>
                  {pickupDate && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-[#2E7D32]">
                      <Calendar className="w-4 h-4" />
                      {lang === 'fr' ? 'Ramassage' : 'Pickup'}: {new Date(pickupDate + 'T12:00:00').toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                  )}
                </div>
              )}
              {!selectedPkg && <p className="text-sm text-[#555555]">{lang === 'fr' ? 'Sélectionnez un forfait' : 'Select a package'}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
